import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/auth";
import { ArrowLeft, Upload, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import NexWordmark from "@/components/NexWordmark";
import { resetAllTutorials } from "@/lib/tutorials";

// Industry roster + per-industry step titles (must mirror INDUSTRY_FLOWS
// in SandboxPage.jsx). Kept as literals here so this page is self-contained.
const INDUSTRIES = [
  { key: "field_services",  label: "Field Services · Trades",     accent: "border-cyan-500/40 text-cyan-300 bg-cyan-500/5" },
  { key: "legal_finance",   label: "Legal · Financial · Admin",  accent: "border-violet-500/40 text-violet-300 bg-violet-500/5" },
  { key: "health_wellness", label: "Health · Wellness · Care",   accent: "border-emerald-500/40 text-emerald-300 bg-emerald-500/5" },
];

const STEP_TITLES = {
  field_services: [
    "Inbound Service Request",       "Dispatch & Route Scheduling",
    "On-Site Inspection",            "Digital Estimate · Sign-Off",
    "Job Execution · Parts Tracking","Customer Sign-Off · After-Photos",
    "On-Site Invoice Generation",    "Immediate Payment Capture",
    "Deposit · Ledger · GL Sync",    "Closeout · Review · Recurring",
  ],
  legal_finance: [
    "New Matter · Inquiry Intake",   "Conflict Check · KYC",
    "Engagement Letter · Retainer",  "Discovery · Case Prep",
    "Evidence · Brief Filing",       "Court Dates · Client Meetings",
    "Billable Time · Expense Capture","Trust Draw · Bill Generation",
    "Payment · Retainer Refill",     "Matter Closure · Client Retention",
  ],
  health_wellness: [
    "Patient Referral · Inquiry",    "Insurance Verification · HIPAA",
    "Session Scheduling",            "Evaluation · Treatment Plan",
    "Session Delivery · Progress Notes","Progress Photos · Outcome Logs",
    "Superbill · Charge Capture",    "Copay · Claim Submission",
    "Revenue Cycle · Posting",       "Discharge · Wellness Plan",
  ],
};

// Build the full 34-slot roster: 1 shared main + 3 industries × (1 industry overview + 10 steps)
const MAIN_SLOT = { key: "main.overview", label: "Main · Platform Welcome", hint: "First-ever visit intro · plays across every industry", industry: "main", isMain: true };
const buildSlots = () => {
  const out = [];
  INDUSTRIES.forEach(({ key: ind, label }) => {
    out.push({ key: `${ind}.overview`, label: `${label} · Industry Overview`, hint: "Plays the first time a prospect switches to this vertical", industry: ind, isOverview: true });
    STEP_TITLES[ind].forEach((title, i) => {
      out.push({ key: `${ind}.step_${i + 1}`, label: `Step ${String(i + 1).padStart(2, "0")} · ${title}`, hint: "Plays inside the side panel when the step opens", industry: ind, step: i + 1 });
    });
  });
  return out;
};

const TutorialManager = () => {
  const [items, setItems] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const [activeInd, setActiveInd] = useState("field_services");
  const slots = buildSlots();

  const load = async () => {
    try {
      const { data } = await apiClient.get("/tutorials");
      setItems(data.items || {});
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (key, file, title) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) return toast.error("Max 100 MB per clip");
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("key", key);
      fd.append("title", title || "");
      await apiClient.post("/admin/tutorials/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Clip uploaded — auto-plays for prospects next visit");
      resetAllTutorials();
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyKey(null); }
  };

  const remove = async (key) => {
    if (!window.confirm("Delete this tutorial clip?")) return;
    try {
      await apiClient.delete(`/admin/tutorials/${key}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadedCountForInd = (ind) =>
    slots.filter(s => s.industry === ind && items[s.key]).length;

  const visibleSlots = slots.filter(s => s.industry === activeInd);

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <div className="border-b border-nex-border bg-black/40 backdrop-blur">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-4 flex items-center gap-3">
          <Link to="/admin/previews" className="flex items-center gap-2 text-nex-chrome hover:text-nex-gold">
            <ArrowLeft size={14} /> <NexWordmark size="text-xl" />
          </Link>
          <span className="mono-body text-[10px] tracking-[0.25em] uppercase text-nex-gold">// TUTORIAL VIDEOS</span>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-8">
        <div className="mb-6">
          <div className="mono-label mb-2">// 15-SEC WALKTHROUGH LIBRARY · 34 SLOTS</div>
          <h1 className="serif-head text-3xl">Sandbox Tutorial Videos</h1>
          <p className="text-nex-chrome mt-1 text-sm">
            1 platform welcome + 3 industry overviews + 30 step tours (10 per industry). Max 100 MB · .mp4/.webm/.mov · 15-30s.
          </p>
        </div>

        {/* Shared main platform slot — always visible above the industry tabs */}
        <div className="mb-6">
          <div className="mono-body text-[10px] uppercase tracking-widest text-nex-gold mb-2">// SHARED · PLATFORM WELCOME</div>
          <div className="grid md:grid-cols-2 gap-4">
            <TutorialSlot
              slot={MAIN_SLOT}
              current={items[MAIN_SLOT.key]}
              busy={busyKey === MAIN_SLOT.key}
              onUpload={(file, title) => upload(MAIN_SLOT.key, file, title)}
              onRemove={() => remove(MAIN_SLOT.key)}
            />
          </div>
        </div>

        {/* Industry tabs */}
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="industry-tabs">
          {INDUSTRIES.map(i => {
            const done = uploadedCountForInd(i.key);
            const active = activeInd === i.key;
            return (
              <button
                key={i.key}
                onClick={() => setActiveInd(i.key)}
                data-testid={`tab-${i.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 border transition-colors mono-body text-[11px] uppercase tracking-widest ${
                  active ? `${i.accent} border-current` : "border-nex-border text-nex-chrome hover:text-nex-gold hover:border-nex-gold"
                }`}
              >
                {i.label}
                <span className="text-[9px] px-1.5 py-0.5 bg-black/40 rounded">
                  {done}/11
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {visibleSlots.map(s => (
            <TutorialSlot
              key={s.key}
              slot={s}
              current={items[s.key]}
              busy={busyKey === s.key}
              onUpload={(file, title) => upload(s.key, file, title)}
              onRemove={() => remove(s.key)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const TutorialSlot = ({ slot, current, busy, onUpload, onRemove }) => {
  const inputRef = useRef(null);
  const [title, setTitle] = useState(current?.title || slot.label);
  useEffect(() => { if (current?.title) setTitle(current.title); }, [current?.title]);

  return (
    <div className={`card-industrial corner-tick p-5 ${slot.isOverview ? "border-l-4 border-l-nex-gold" : ""}`} data-testid={`tut-slot-${slot.key}`}>
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="min-w-0">
          <div className="mono-body text-[10px] uppercase tracking-widest text-nex-muted truncate">{slot.key}</div>
          <div className="text-nex-silver font-semibold text-sm">{slot.label}</div>
          <div className="mono-body text-[10px] text-nex-muted mt-0.5">{slot.hint}</div>
        </div>
        {current
          ? <span className="mono-body text-[9px] uppercase tracking-widest px-2 py-1 bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 flex items-center gap-1 shrink-0"><CheckCircle2 size={11} /> Live</span>
          : <span className="mono-body text-[9px] uppercase tracking-widest px-2 py-1 border border-nex-border text-nex-muted shrink-0">Empty</span>}
      </div>

      {current && (
        <video src={current.url} poster="/nex-mark.png" preload="metadata" controls className="w-full aspect-video bg-black rounded-md mb-3 border border-nex-border/40" />
      )}

      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Clip title (shown to prospects)" className="input-industrial !py-2 text-xs mb-2" data-testid={`tut-title-${slot.key}`} />

      <input
        type="file"
        accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
        ref={inputRef}
        onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f, title); e.target.value = ""; }}
        className="hidden"
        data-testid={`tut-file-${slot.key}`}
      />
      <div className="flex gap-2">
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`tut-upload-${slot.key}`}
          className="tick-btn-primary text-xs !py-2 !px-3 flex-1"
        >
          {busy ? <><Loader2 size={12} className="animate-spin" /> Uploading</> : <><Upload size={12} /> {current ? "Replace clip" : "Upload clip"}</>}
        </button>
        {current && (
          <button onClick={onRemove} data-testid={`tut-delete-${slot.key}`} className="tick-btn-secondary text-xs !py-2 !px-3 text-red-300 hover:border-red-400">
            <X size={12} />
          </button>
        )}
      </div>
      {current && (
        <div className="mono-body text-[9px] text-nex-muted mt-2">
          {(current.size / (1024 * 1024)).toFixed(1)} MB · uploaded by {current.uploaded_by}
        </div>
      )}
    </div>
  );
};

export default TutorialManager;
